/**
 * Composer Enter-key policy (owner report 2026-09-07: items typed on separate
 * lines ran together; on phones Enter could only SEND).
 *
 * One pure decision shared by the chat composer (useGlobalChat.handleKeyDown)
 * and the landing PromptBar so both keyboards behave the same:
 *
 *   • Enter mid-IME-composition never sends (isComposing / keyCode 229 — the
 *     composition-in-progress code every mobile and CJK keyboard reports).
 *   • Shift+Enter never sends (browser inserts the line break).
 *   • On a COARSE primary pointer (phones, tablets) Enter inserts a newline —
 *     soft keyboards have no Shift chord — and the always-present send button
 *     sends. Modality, not viewport width, is the signal: a phone in landscape
 *     is still a phone, a narrow desktop window still has a keyboard.
 *   • Otherwise Enter sends.
 *
 * Capability detection only — no product decision lives here (D8/D9).
 */
export interface EnterKeyFacts {
  key: string;
  shiftKey?: boolean;
  isComposing?: boolean;
  keyCode?: number;
  /** Primary pointing device is coarse (touch). */
  coarsePointer: boolean;
}

export const IME_COMPOSITION_KEYCODE = 229;

export function enterShouldSend(facts: EnterKeyFacts): boolean {
  if (facts.key !== 'Enter') return false;
  if (facts.isComposing === true || facts.keyCode === IME_COMPOSITION_KEYCODE) return false;
  if (facts.shiftKey === true) return false;
  if (facts.coarsePointer) return false;
  return true;
}

type MatchMediaHost = { matchMedia?: (q: string) => { matches: boolean } };

/**
 * True when the primary pointer is coarse. Falls back to the caller's
 * viewport bit when matchMedia is unavailable (older webviews, jsdom).
 */
export function isCoarsePointer(
  host: MatchMediaHost | undefined = typeof window !== 'undefined' ? (window as MatchMediaHost) : undefined,
  fallbackMobileViewport = false,
): boolean {
  try {
    if (host && typeof host.matchMedia === 'function') {
      return host.matchMedia('(pointer: coarse)').matches === true;
    }
  } catch {
    // fall through to the viewport fallback
  }
  return fallbackMobileViewport === true;
}
